import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [attendanceStatuses, setAttendanceStatuses] = useState({});
  
  const { user } = useUser();
  
  const backendUrl = process.env.NODE_ENV === 'production' 
    ? "https://slam-backend.onrender.com" 
    : "http://localhost:8080";

  // 안전한 이미지 URL 생성 (절대경로면 그대로, 상대경로면 backendUrl을 붙임)
  const resolveImageUrl = (url) => {
    if (!url) return "/default_event_image.jpg";
    return /^https?:\/\//i.test(url) ? url : `${backendUrl}${url}`;
  };

  // ❌ const userMemberships = ['NCCU']; // ⬅️ 임시 데이터를 삭제합니다.

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const endpoint = filter === 'All' ? '/api/events' : `/api/events?branch=${filter}`;
        const response = await axios.get(endpoint);
        
        // ✅ 이벤트 날짜 기준 24시간 후까지 표시 + 보관(archived) 제외
        const now = new Date();
        const futureEvents = response.data.filter(event => {
          if (event.archived) return false;
          const eventDate = new Date(event.eventDateTime);
          const eventDatePlus24h = new Date(eventDate.getTime() + 24 * 60 * 60 * 1000); // 24시간 후
          return eventDatePlus24h > now;
        });
        setEvents(futureEvents);
        
        // ✅ 로그인된 사용자의 경우 참석 상태 확인
        if (user?.isLoggedIn) {
          const statusPromises = futureEvents.map(async (event) => {
            try {
              const statusResponse = await axios.get(`/api/events/${event.id}/attendance-status`);
              return { eventId: event.id, status: statusResponse.data };
            } catch (error) {
              return { eventId: event.id, status: { status: 'NOT_ATTENDING' } };
            }
          });
          
          const statuses = await Promise.all(statusPromises);
          const statusMap = {};
          statuses.forEach(({ eventId, status }) => {
            statusMap[eventId] = status;
          });
          setAttendanceStatuses(statusMap);
        }
      } catch (error) {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [filter, user?.isLoggedIn]);

  const branches = ['All', 'TAIPEI', 'NCCU', 'NTU'];

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const isStaff = user?.isLoggedIn && ['ADMIN', 'STAFF', 'PRESIDENT'].includes(user.role);

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
        <div className="text-center">
          <h1 className="text-4xl font-black text-gray-900 sm:text-5xl">Upcoming Events</h1>
          <p className="mt-4 text-xl text-gray-600">
            ✨ This is where the magic happens.
            <br className="sm:hidden" />
            <span className="hidden sm:inline"> </span>
            Find your next adventure. ✨
          </p>
        </div>

        <div className="flex justify-center gap-2 sm:gap-4 my-12">
          {branches.map(branch => (
            <button 
              key={branch}
              onClick={() => setFilter(branch)}
              className={`px-4 sm:px-6 py-2 rounded-full font-semibold text-sm sm:text-base transition-colors ${filter === branch ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-700 hover:bg-gray-200'}`}
            >
              {branch}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Loading events...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map(event => (
              <div key={event.id} className="bg-white rounded-lg shadow-lg flex flex-col">
                <img 
                  src={resolveImageUrl(event.imageUrl)} 
                  alt={event.title} 
                  className="w-full h-48 object-cover rounded-t-lg" 
                  onError={(e) => { e.currentTarget.src = "/default_event_image.jpg"; }}
                />
                <div className="p-6 flex flex-col flex-grow">
                  <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-3 self-start ${
                    event.branch === 'TAIPEI' ? 'bg-green-200 text-green-800' :
                    event.branch === 'NCCU' ? 'bg-blue-200 text-blue-800' : 'bg-purple-200 text-purple-800'
                  }`}>{event.branch}</span>
                  <h2 className="text-xl font-bold mb-2 flex-grow">{event.title}</h2>
                  <p className="text-gray-600 text-sm">{formatDate(event.eventDateTime)}</p>
                  <p className="text-gray-600 text-sm mb-4">{event.location}</p>
                  
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    {user?.isLoggedIn ? (
                      (() => {
                        const status = attendanceStatuses[event.id];
                        
                        // 승인 대기 중인 경우
                        if (status?.status === 'PENDING') {
                          return (
                            <Link to={
                              event.eventType === 'SPECIAL_EVENT' 
                                ? `/events/${event.id}/ticket` 
                                : `/membership?branch=${encodeURIComponent(event.branch || 'NCCU')}`
                            } className="block w-full text-center bg-yellow-500 text-white font-bold py-2 rounded-lg hover:bg-yellow-600 transition-colors">
                              Wait for Approval
                            </Link>
                          );
                        }
                        
                        // 이미 참석 중인 경우
                        if (status?.status === 'ATTENDING') {
                          return (
                            <Link to={`/events/${event.id}`} className="block w-full text-center bg-green-500 text-white font-bold py-2 rounded-lg hover:bg-green-600 transition-colors">
                              ✓ Manage Attendance
                            </Link>
                          );
                        }
                        
                        // 무료 참석 가능한 경우
                        if (event.canJoinForFree) {
                          return (
                            <Link to={`/events/${event.id}`} className="block w-full text-center bg-green-500 text-white font-bold py-2 rounded-lg hover:bg-green-600 transition-colors">
                              Manage Attendance
                            </Link>
                          );
                        }
                        
                        // 결제가 필요한 경우
                        return (
                          <Link to={
                            event.eventType === 'SPECIAL_EVENT' 
                              ? `/events/${event.id}/ticket` 
                              : `/membership?branch=${encodeURIComponent(event.branch || 'NCCU')}&eventId=${event.id}`
                          } className={`block w-full text-center font-bold py-2 rounded-lg transition-colors ${
                            event.productType === 'Ticket' 
                              ? 'bg-orange-500 text-white hover:bg-orange-600' 
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}>
                            {event.joinButtonText || `Join ${event.productType || 'Membership'}`}
                          </Link>
                        );
                      })()
                    ) : (
                      <Link to="/login" className="block w-full text-center bg-gray-600 text-white font-bold py-2 rounded-lg hover:bg-gray-700 transition-colors">
                        Log in to Join
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}